import { useAuth, useClerk } from '@clerk/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BarChart3, Boxes, Check, Copy, Eye, EyeOff, KeyRound, LoaderCircle, LockKeyhole, PackageCheck,
  Search, Send, ShieldAlert, Star, Trash2, TriangleAlert, UsersRound, X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Header } from '../components/Header'
import { Modal } from '../components/Modal'
import { ProductImage } from '../components/ProductImage'
import { apiRequest, formatDate, formatMoney, orderStatusLabels, planLabels, planShortLabels } from '../lib/api'
import {
  LICENSE_CATEGORIES,
  type AccountProfile, type AdminKey, type AdminOrderDetail, type AdminOverview, type AdminReview,
  type KeyStats, type Plan, type Product, type ProductStatus,
} from '../types'

type AdminTab = 'overview' | 'restock' | 'orders' | 'reviews'

const EMPTY_STATS: KeyStats = { ready: { weekly: 0, monthly: 0, lifetime: 0 }, delivered: 0 }

function readyFor(stats: KeyStats | undefined, plan: Plan) {
  return stats?.ready?.[plan] ?? 0
}

function planCatLabel(plan: Plan) {
  return LICENSE_CATEGORIES.find((c) => c.plan === plan)?.label ?? planShortLabels[plan]
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      className="icon-button"
      type="button"
      title="Copy"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true)
          window.setTimeout(() => setCopied(false), 1400)
        })
      }}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  )
}

/* ---------- Manage product (details + keys per category) ---------- */

function ManageProductModal({ product, stats, onClose }: { product: Product; stats: KeyStats; onClose: () => void }) {
  const queryClient = useQueryClient()
  const { getToken } = useAuth()
  const offeredPlans = product.plans.length > 0 ? product.plans : (['monthly'] as Plan[])
  const [category, setCategory] = useState<Plan>(offeredPlans[0])
  const [keysText, setKeysText] = useState('')

  // Product detail fields (stock is no longer editable — keys ARE the stock)
  const [prices, setPrices] = useState<Record<Plan, string>>({
    weekly: (product.pricing.weekly / 100).toFixed(2),
    monthly: (product.pricing.monthly / 100).toFixed(2),
    lifetime: (product.pricing.lifetime / 100).toFixed(2),
  })
  const [status, setStatus] = useState<ProductStatus>(product.status)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-overview'] })
    void queryClient.invalidateQueries({ queryKey: ['admin-keys', product.id] })
  }

  const keys = useQuery({
    queryKey: ['admin-keys', product.id],
    queryFn: async () =>
      apiRequest<{ data: { keys: AdminKey[] } }>(`/api/admin/products/${product.id}/keys`, { token: await getToken() }),
  })

  const addKeys = useMutation({
    mutationFn: async () => {
      const list = keysText.split('\n').map((line) => line.trim()).filter(Boolean)
      if (list.length === 0) throw new Error('Paste at least one key (one per line).')
      return apiRequest<{ data: { added: number } }>(`/api/admin/products/${product.id}/keys`, {
        method: 'POST',
        token: await getToken(),
        json: { keys: list, duration: category },
      })
    },
    onSuccess: () => {
      setKeysText('')
      refresh()
    },
  })

  const deleteKey = useMutation({
    mutationFn: async (id: string) =>
      apiRequest<{ data: { deleted: boolean } }>(`/api/admin/keys/${id}`, { method: 'DELETE', token: await getToken() }),
    onSuccess: refresh,
  })

  const saveDetails = useMutation({
    mutationFn: async () =>
      apiRequest<{ data: { updated: boolean } }>(`/api/admin/products/${product.id}`, {
        method: 'PATCH',
        token: await getToken(),
        json: {
          weeklyPriceCents: Math.round(Number(prices.weekly) * 100),
          monthlyPriceCents: Math.round(Number(prices.monthly) * 100),
          lifetimePriceCents: Math.round(Number(prices.lifetime) * 100),
          status,
        },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin-overview'] }),
  })

  const allKeys = keys.data?.data.keys ?? []
  const available = allKeys.filter((key) => key.status === 'available' && key.duration === category)
  const sent = allKeys.filter((key) => key.status === 'delivered' && key.duration === category)
  const pasteCount = keysText.split('\n').map((line) => line.trim()).filter(Boolean).length

  return (
    <Modal open onClose={onClose} title={`Manage ${product.name}`} eyebrow={`PRODUCT // ${product.id.toUpperCase()}`} size="wide">
      <div className="manage">
        {/* Category selector — only the plans this product sells */}
        <div className="manage__cats" role="tablist" aria-label="License category">
          {offeredPlans.map((plan) => (
            <button
              key={plan}
              role="tab"
              aria-selected={category === plan}
              className="manage__cat"
              data-active={category === plan}
              onClick={() => setCategory(plan)}
            >
              {planCatLabel(plan)}
              <span data-empty={readyFor(stats, plan) === 0}>{readyFor(stats, plan)}</span>
            </button>
          ))}
        </div>

        {/* Bulk add for the selected category */}
        <div className="manage__restock">
          <label className="manage__field">
            <span>Paste {planCatLabel(category)} keys — one per line (added ready to deliver)</span>
            <textarea
              value={keysText}
              onChange={(event) => setKeysText(event.target.value)}
              rows={4}
              spellCheck={false}
              placeholder={'VOLT-XXXX-XXXX-XXXX\nVOLT-YYYY-YYYY-YYYY'}
            />
          </label>
          <div className="manage__restock-foot">
            <span>{addKeys.isSuccess ? <><Check size={13} /> {addKeys.data.data.added} added</> : addKeys.error ? addKeys.error.message : `${pasteCount} to add · duplicates ignored`}</span>
            <button className="button button--primary button--small" type="button" onClick={() => addKeys.mutate()} disabled={addKeys.isPending || pasteCount === 0}>
              {addKeys.isPending ? <LoaderCircle className="spin" size={14} /> : <KeyRound size={14} />} Add {pasteCount || ''} keys
            </button>
          </div>
        </div>

        {/* Available + sent lists for the selected category */}
        <div className="manage__keys">
          <div className="manage__keys-col">
            <h4>Available <span>{available.length}</span></h4>
            <div className="keychip-list">
              {available.length === 0 ? <p className="keychip-empty">No keys in stock for this category.</p> : available.map((key) => (
                <div className="keychip" key={key.id}>
                  <code>{key.value}</code>
                  {key.reserved ? <span className="keychip__order">in checkout</span> : null}
                  <CopyButton value={key.value} />
                  {!key.reserved ? (
                    <button className="icon-button" type="button" title="Delete" onClick={() => deleteKey.mutate(key.id)} disabled={deleteKey.isPending}><Trash2 size={12} /></button>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <div className="manage__keys-col">
            <h4>Sent <span>{sent.length}</span></h4>
            <div className="keychip-list">
              {sent.length === 0 ? <p className="keychip-empty">Delivered keys will appear here.</p> : sent.map((key) => (
                <div className="keychip keychip--sent" key={key.id}>
                  <code>{key.value}</code>
                  {key.orderId ? <span className="keychip__order">#{key.orderId.slice(-8).toUpperCase()}</span> : null}
                  {key.deliveredAt ? <small>{formatDate(key.deliveredAt)}</small> : null}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product details */}
        <details className="manage__details">
          <summary>Pricing &amp; visibility</summary>
          <div className="inventory-form-grid">
            {offeredPlans.map((plan) => (
              <label key={plan}>{planCatLabel(plan)} price (USD)
                <input type="number" min="0.50" step="0.01" value={prices[plan]} onChange={(e) => setPrices((p) => ({ ...p, [plan]: e.target.value }))} />
              </label>
            ))}
            <label>Visibility<select value={status} onChange={(e) => setStatus(e.target.value as ProductStatus)}><option value="active">Active</option><option value="draft">Draft</option><option value="archived">Archived</option></select></label>
          </div>
          <div className="manage__details-foot">
            <span>{saveDetails.isSuccess ? <><Check size={13} /> Saved</> : saveDetails.error ? saveDetails.error.message : 'Stock shown in the store = keys available per category.'}</span>
            <button className="button button--secondary button--small" type="button" onClick={() => saveDetails.mutate()} disabled={saveDetails.isPending}>
              {saveDetails.isPending ? <LoaderCircle className="spin" size={14} /> : null} Save details
            </button>
          </div>
        </details>
      </div>
    </Modal>
  )
}

/* ---------- Restock tab: product table ---------- */

function RestockPanel({ data }: { data: AdminOverview }) {
  const [manage, setManage] = useState<Product | null>(null)

  return (
    <div className="admin-panel t-panel-reveal">
      <div className="subsection-heading">
        <div><span className="eyebrow">INVENTORY</span><h2>Restock keys</h2></div>
        <span>{data.products.length} {data.products.length === 1 ? 'product' : 'products'}</span>
      </div>

      <div className="restock-table">
        <div className="restock-table__head">
          <span>Product</span>
          <span>Keys ready</span>
          <span>Price</span>
          <span>Status</span>
          <span />
        </div>
        {data.products.map((product) => {
          const stats = data.keyStats[product.id]
          const plans = product.plans.length > 0 ? product.plans : (['monthly'] as Plan[])
          return (
            <div className="restock-table__row" key={product.id}>
              <div className="restock-product">
                <span style={{ ['--accent' as string]: product.accent }}><ProductImage name={product.name} /></span>
                <div><strong>{product.name}</strong><small>{product.tag ?? product.slug}</small></div>
              </div>
              <div className="restock-keys">
                {plans.map((plan) => {
                  const ready = readyFor(stats, plan)
                  return (
                    <span className="restock-count" data-state={ready === 0 ? 'out' : ready <= 5 ? 'low' : 'ok'} key={plan}>
                      <KeyRound size={10} /> {planShortLabels[plan]} · {ready}
                    </span>
                  )
                })}
              </div>
              <span className="restock-table__price">
                {plans.map((plan) => formatMoney(product.pricing[plan], product.pricing.currency)).join(' · ')}
              </span>
              <span className="status-badge" data-status={product.status === 'active' ? 'active' : 'paused'}><i />{product.status}</span>
              <button className="button button--secondary button--small" type="button" onClick={() => setManage(product)}>
                <Boxes size={14} /> Manage
              </button>
            </div>
          )
        })}
      </div>

      {manage ? (
        <ManageProductModal product={manage} stats={data.keyStats[manage.id] ?? EMPTY_STATS} onClose={() => setManage(null)} />
      ) : null}
    </div>
  )
}

/* ---------- Orders tab: search + track ---------- */

function OrdersPanel() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [term, setTerm] = useState('')
  const [query, setQuery] = useState('')

  const orders = useQuery({
    queryKey: ['admin-orders', query],
    queryFn: async () =>
      apiRequest<{ data: AdminOrderDetail[] }>(`/api/admin/orders${query ? `?q=${encodeURIComponent(query)}` : ''}`, {
        token: await getToken(),
      }),
  })

  const deliver = useMutation({
    mutationFn: async (orderId: string) =>
      apiRequest<{ data: { delivered: boolean } }>(`/api/admin/orders/${orderId}/deliver`, { method: 'POST', token: await getToken() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-orders'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-overview'] })
    },
  })

  const rows = orders.data?.data ?? []

  return (
    <div className="admin-panel t-panel-reveal">
      <div className="subsection-heading">
        <div><span className="eyebrow">FULFILLMENT</span><h2>Orders</h2></div>
        <span>{rows.length} shown</span>
      </div>

      <form
        className="order-search"
        onSubmit={(event) => {
          event.preventDefault()
          setQuery(term.trim())
        }}
      >
        <Search size={15} />
        <input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Search by order ID (e.g. 4D1DC54A)"
          spellCheck={false}
        />
        {term ? <button type="button" className="icon-button" onClick={() => { setTerm(''); setQuery('') }} aria-label="Clear"><X size={14} /></button> : null}
        <button className="button button--secondary button--small" type="submit">Search</button>
      </form>

      {deliver.error ? <div className="form-message form-message--error">{deliver.error.message}</div> : null}

      {orders.isLoading ? (
        <div className="dashboard-loading"><LoaderCircle className="spin" /></div>
      ) : rows.length === 0 ? (
        <div className="admin-empty">{query ? `No orders match “${query}”.` : 'No orders yet.'}</div>
      ) : (
        <div className="order-track">
          {rows.map((order) => (
            <article className="order-track__row" data-delivery={order.deliveryStatus} key={order.id}>
              <div className="order-track__ref">
                <strong>#{order.id.slice(-8).toUpperCase()}</strong>
                <small>{formatDate(order.createdAt)}</small>
              </div>
              <div className="order-track__product">
                <strong>{order.productName}</strong>
                <small>{planLabels[order.plan]} · {formatMoney(order.amount, order.currency)}</small>
              </div>
              <div className="order-track__customer">{order.email ?? <span className="muted">—</span>}</div>
              <span className="status-badge" data-status={order.status}><i />{orderStatusLabels[order.status]}</span>
              <div className="order-track__key">
                {order.keyValue ? (
                  <><code>{order.keyValue}</code><CopyButton value={order.keyValue} /></>
                ) : order.status === 'paid' ? (
                  <button className="button button--primary button--small" type="button" onClick={() => deliver.mutate(order.id)} disabled={deliver.isPending}>
                    {deliver.isPending ? <LoaderCircle className="spin" size={13} /> : <Send size={13} />} Deliver
                  </button>
                ) : <span className="muted">—</span>}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  )
}

/* ---------- Overview ---------- */

function OverviewPanel({ data, onDeliver, delivering, deliverError }: {
  data: AdminOverview
  onDeliver: (orderId: string) => void
  delivering: boolean
  deliverError: string | null
}) {
  return (
    <div className="admin-panel t-panel-reveal">
      <div className="admin-stats">
        <article><span><PackageCheck /></span><small>ORDERS</small><strong>{data.stats.orders}</strong><p>All-time total</p></article>
        <article><span><UsersRound /></span><small>LICENSES SOLD</small><strong>{data.stats.subscriptions}</strong><p>Paid orders</p></article>
        <article><span><BarChart3 /></span><small>REVENUE</small><strong>{formatMoney(data.stats.bookedCents)}</strong><p>Paid, all-time</p></article>
        <article><span><Star /></span><small>REVIEWS</small><strong>{data.stats.reviews}</strong><p>{data.stats.publishedReviews} published</p></article>
      </div>

      {data.pendingDeliveries.length ? (
        <section className="admin-table-card admin-table-card--alert">
          <div className="subsection-heading"><div><span className="eyebrow">ACTION NEEDED</span><h2>Pending deliveries</h2></div><span>{data.pendingDeliveries.length} waiting</span></div>
          <p className="admin-hint"><TriangleAlert size={13} /> Paid orders with no matching key yet. Add keys in Restock, then deliver.</p>
          <div className="moderation-list">
            {data.pendingDeliveries.map((order) => (
              <article className="moderation-card" key={order.id}>
                <div><h3>#{order.id.slice(-8).toUpperCase()} · {order.productName}</h3><p>{planLabels[order.plan]} · {formatMoney(order.amount, order.currency)} · paid {formatDate(order.createdAt)}</p></div>
                <button className="button button--primary button--small" onClick={() => onDeliver(order.id)} disabled={delivering}>
                  {delivering ? <LoaderCircle className="spin" size={14} /> : <Send size={14} />} Deliver
                </button>
              </article>
            ))}
          </div>
          {deliverError ? <div className="form-message form-message--error">{deliverError}</div> : null}
        </section>
      ) : null}
    </div>
  )
}

/* ---------- Reviews ---------- */

function ReviewsPanel() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const reviews = useQuery({
    queryKey: ['admin-reviews'],
    queryFn: async () => apiRequest<{ data: AdminReview[] }>('/api/admin/reviews', { token: await getToken() }),
  })
  const moderate = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'published' | 'hidden' }) =>
      apiRequest<{ data: { updated: boolean } }>(`/api/admin/reviews/${id}`, { method: 'PATCH', token: await getToken(), json: { status } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin-reviews'] }),
  })

  return (
    <div className="admin-panel t-panel-reveal">
      <div className="subsection-heading"><div><span className="eyebrow">MODERATION</span><h2>Customer reviews</h2></div><span>{reviews.data?.data.length ?? 0} entries</span></div>
      {reviews.isLoading ? <div className="dashboard-loading"><LoaderCircle className="spin" /></div> : reviews.data?.data.length ? (
        <div className="moderation-list">
          {reviews.data.data.map((review) => (
            <article className="moderation-card" key={review.id}>
              <div className="review-card__stars">{[1, 2, 3, 4, 5].map((v) => <Star key={v} size={12} fill={v <= review.rating ? 'currentColor' : 'none'} />)}</div>
              <div><p>{review.body || <em className="muted">No comment</em>}</p><span>{review.displayName} · {review.productName} · {formatDate(review.createdAt)}</span></div>
              <button className="button button--ghost button--small" onClick={() => moderate.mutate({ id: review.id, status: review.status === 'published' ? 'hidden' : 'published' })} disabled={moderate.isPending}>
                {review.status === 'published' ? <><EyeOff size={14} /> Hide</> : <><Eye size={14} /> Publish</>}
              </button>
            </article>
          ))}
        </div>
      ) : <div className="admin-empty">No reviews to moderate.</div>}
    </div>
  )
}

/* ---------- Page ---------- */

export function AdminPage() {
  const [tab, setTab] = useState<AdminTab>('overview')
  const { isLoaded, isSignedIn, userId, getToken } = useAuth()
  const { openSignIn } = useClerk()
  const queryClient = useQueryClient()

  const profile = useQuery({
    queryKey: ['account', userId],
    queryFn: async () => apiRequest<{ data: AccountProfile }>('/api/me', { token: await getToken() }),
    enabled: Boolean(isLoaded && isSignedIn),
    retry: false,
  })
  const isAdmin = profile.data?.data.isAdmin === true

  const overview = useQuery({
    queryKey: ['admin-overview'],
    queryFn: async () => apiRequest<{ data: AdminOverview }>('/api/admin/overview', { token: await getToken() }),
    enabled: isAdmin,
  })

  const deliver = useMutation({
    mutationFn: async (orderId: string) =>
      apiRequest<{ data: { delivered: boolean } }>(`/api/admin/orders/${orderId}/deliver`, { method: 'POST', token: await getToken() }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin-overview'] }),
  })

  const data = overview.data?.data
  const tabs = useMemo(
    () => [
      { id: 'overview' as const, label: 'Overview', icon: <BarChart3 size={15} /> },
      { id: 'restock' as const, label: 'Restock', icon: <Boxes size={15} /> },
      { id: 'orders' as const, label: 'Orders', icon: <PackageCheck size={15} /> },
      { id: 'reviews' as const, label: 'Reviews', icon: <Star size={15} /> },
    ],
    [],
  )

  return (
    <div className="page-shell">
      <Header />
      <main className="admin-page section-frame">
        {!isLoaded || (isSignedIn && profile.isLoading) ? (
          <div className="dashboard-loading"><LoaderCircle className="spin" /><span>Checking permissions…</span></div>
        ) : !isSignedIn ? (
          <section className="auth-gate"><div className="auth-gate__icon"><LockKeyhole /></div><span className="eyebrow">ADMINISTRATION</span><h1>Restricted access.</h1><p>Sign in with an admin account to continue.</p><button className="button button--primary" onClick={() => void openSignIn({})}>Sign in</button></section>
        ) : !isAdmin ? (
          <section className="auth-gate"><div className="auth-gate__icon auth-gate__icon--danger"><ShieldAlert /></div><span className="eyebrow">403 // RESTRICTED</span><h1>Not authorized.</h1><p>Your identity is valid, but it cannot manage CheatsEclipse.</p></section>
        ) : (
          <>
            <section className="admin-heading"><div><span className="eyebrow">MISSION CONTROL</span><h1>CheatsEclipse <em>Admin.</em></h1><p>Inventory, keys, orders and trust from one place.</p></div><span className="admin-live"><i /> SYSTEM OPERATIONAL</span></section>

            <nav className="admin-tabs" data-tab={tab} aria-label="Admin sections">
              <span className="admin-tabs__pill" style={{ ['--tab-index' as string]: tabs.findIndex((t) => t.id === tab) }} />
              {tabs.map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)} aria-selected={tab === t.id}>{t.icon} {t.label}</button>
              ))}
            </nav>

            {overview.isLoading ? <div className="dashboard-loading"><LoaderCircle className="spin" /><span>Loading…</span></div> : overview.error ? <div className="form-message form-message--error">{overview.error.message}</div> : null}

            {data && tab === 'overview' ? <OverviewPanel data={data} onDeliver={(id) => deliver.mutate(id)} delivering={deliver.isPending} deliverError={deliver.error?.message ?? null} /> : null}
            {data && tab === 'restock' ? <RestockPanel data={data} /> : null}
            {tab === 'orders' ? <OrdersPanel /> : null}
            {tab === 'reviews' ? <ReviewsPanel /> : null}
          </>
        )}
      </main>
    </div>
  )
}
